import { Metric } from '@strength/domain';
import {
  getMetricSpec,
  SetMetricMutationData,
  WorkoutExercise,
  WorkoutExerciseMetric,
  WorkoutSet,
} from '@strength/domain-workout';
import { CustomSlot } from '../../../../components/sheet/slots';
import { CellInputWithAtom } from '../../../../components/sheet/slots/common';
import { jotaiStore } from '../../../../utils/jotai';
import { inputAtom } from '../../slots/common';
import { BlockSummarySelectionData } from '../types';

export const BaseWorkoutSlot = (
  x: number,
  y: number,
  value: string,
  metric: WorkoutExerciseMetric,
  exercise: WorkoutExercise,
  set: WorkoutSet,
  onEditSetMetric: (
    workoutId: string,
    workoutSetId: string,
    data: SetMetricMutationData,
  ) => void,
  getMetric: (metricId: string) => Metric,
  disabled: boolean,
  disableContextMenu: boolean,
  tooltipIcon: React.ReactNode,
) => {
  const metricConfig = getMetric(metric.metricId);
  return new CustomSlot<BlockSummarySelectionData>(
    { x, y },
    1,
    1,
    disabled,
    (editing, onBlur) => {
      if (editing && !disabled) {
        return <CellInputWithAtom inputAtom={inputAtom} onBlur={onBlur} />;
      }
      return (
        <WorkoutSetMetricCellContent
          metricInput={value}
          metric={metricConfig}
          tooltipIcon={tooltipIcon}
        />
      );
    },
    {
      className: 'flex flex-col w-[36px] items-center justify-center truncate',
      data: [
        {
          type: 'Set',
          workoutId: exercise.workoutId,
          exercise: exercise,
          set: set,
          metrics: [metric],
          disabled: disabled,
        },
      ],
      disableContextMenu,
      editBehavior: {
        type: 'input',
        onStart: (initInput) => {
          let input: string;
          if (initInput !== undefined) {
            input = initInput;
          } else if (value) {
            input = value;
          } else {
            input = '';
          }
          jotaiStore.set(inputAtom, input);
        },
        onCommit: () => {
          const input = jotaiStore.get(inputAtom);
          // Positive integer, or empty when cleared / non-matching input
          const normalized = input.trim();
          const sanitizedInput = /^[1-9]\d*$/.test(normalized)
            ? normalized
            : '';
          onEditSetMetric(exercise.workoutId, set.workoutSetId, {
            metricId: metric.metricId,
            input: sanitizedInput,
          });
          jotaiStore.set(inputAtom, '');
        },
      },
    },
  );
};

export const WorkoutSetMetricCellContent = ({
  metricInput,
  metric,
  tooltipIcon,
}: {
  metricInput: string;
  metric: Metric;
  tooltipIcon: React.ReactNode;
}) => {
  const m = metric;
  const spec = getMetricSpec(m);
  const valueParseResult = spec.schema.safeParse(metricInput);
  const value = valueParseResult.success ? valueParseResult.data : '';

  if (tooltipIcon === undefined) {
    return <>{spec.displayValue(value)}</>;
  }

  return (
    <span className="relative grid w-full min-w-0 [grid-template-areas:'content']">
      <span className="grid-in-[content] flex items-center justify-center truncate">
        {spec.displayValue(value)}
      </span>
      {tooltipIcon && (
        <span className="absolute top-[-4.5px] right-0 pointer-events-auto">
          {tooltipIcon}
        </span>
      )}
    </span>
  );
};
